/**
 * Normalize FastAPI errors so we never pass an object into `toast.error()`
 * (which React would try to render as a child → "Objects are not valid as
 * a React child" crash).
 *
 * FastAPI returns several shapes:
 *   - `{detail: "string"}`             — raised HTTPException
 *   - `{detail: [{loc, msg, type}, ...]}` — pydantic validation array
 *   - `{detail: {reason, organization_id, ...}}` — structured business
 *     errors (e.g. insufficient_funds_http_detail). When the detail is
 *     an object with a `reason` field, we surface that.
 */
export function readableApiError(err, fallback = 'Something went wrong') {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((v) => {
        if (typeof v === 'string') return v;
        const field = Array.isArray(v?.loc) ? v.loc.filter((p) => p !== 'body').join('.') : '';
        const msg = v?.msg || 'invalid value';
        return field ? `${field}: ${msg}` : msg;
      })
      .join(' · ');
  }
  // Structured business error (e.g. insufficient funds)
  if (typeof d === 'object') {
    if (d.reason === 'insufficient_funds' || d.reason === 'insufficient_org_wallet_funds') {
      const need = ((d.required_cents || 0) / 100).toFixed(2);
      const have = ((d.balance_cents || 0) / 100).toFixed(2);
      return `Insufficient wallet balance — need $${need}, have $${have}. Top up to continue.`;
    }
    return d.message || d.msg || d.reason || fallback;
  }
  return String(d);
}

/**
 * Returns `{topup_url, organization_id}` when the error is a structured
 * insufficient-funds 402, otherwise null. Useful for one-click "Top up"
 * recovery on toasts and modals.
 */
export function insufficientFundsInfo(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object' && (d.reason === 'insufficient_funds' || d.reason === 'insufficient_org_wallet_funds')) {
    return {
      topup_url: d.topup_url || '/organization/dashboard?tab=wallet',
      organization_id: d.organization_id,
      required_cents: d.required_cents || 0,
      balance_cents: d.balance_cents || 0,
      shortfall_cents: d.shortfall_cents || Math.max(0, (d.required_cents || 0) - (d.balance_cents || 0)),
      units: d.units,
      unit_kind: d.unit_kind,
    };
  }
  return null;
}
